/** The creative formats a Demand Gen video ad accepts. */
export type VideoFormat = '9:16' | '16:9' | '1:1';

export interface VideoFormatReading {
  /** null when the ratio was read but is none of the three allowed ones. */
  format: VideoFormat | null;
  /** Measured source size, or 0 when the source size could not be read. */
  width: number;
  height: number;
  /** YouTube has no thumbnail for this id at all: deleted, private or mistyped. */
  missing: boolean;
}

interface Measurement {
  width: number;
  height: number;
}

/**
 * Reads a YouTube video's aspect ratio without an API key, by measuring the one
 * thumbnail file YouTube renders at the source's own dimensions:
 * `oardefault.jpg` — "original aspect ratio".
 *
 * Sampled against live YouTube: vertical uploads give 1080x1920 or 720x1280,
 * square ones 1080x1080 / 1008x1008 / 720x720, and 16:9 ones give nothing at all.
 *
 * Three traps this has to work around:
 *
 * 1. A missing `oardefault` answers HTTP 404 but still carries a decodable
 *    120x90 grey JPEG, so the browser paints it and never fires `error`. The
 *    size is the only tell, which is why the sentinel is caught in `onload`.
 * 2. 16:9 is therefore an inference from absence, and a link that resolves to
 *    no video at all looks exactly the same. `hq720.jpg` tells them apart: a
 *    real 16:9 upload has one, a dead id gives the sentinel for that too.
 * 3. Even then 16:9 is not proof — an old or unusual upload YouTube never
 *    generated an original-ratio frame for reads as 16:9. Nothing here
 *    validates; the block warns, the author decides.
 *
 * Structurally this mirrors {@link CreativeImageRouterHelper}, which solves the
 * same problem for uploaded image creatives: measure async, cache by key, match
 * on ratio with a tolerance, and expose `isResolving` so the first paint reads
 * as loading rather than as a missing asset. That handoff relies on zone.js
 * patching the image's load event — if this app ever goes zoneless, both
 * helpers break at once and will need an explicit markForCheck.
 */
export class YoutubeFormatHelper {
  /** Nominal ratios, widest apart first so the match order reads naturally. */
  private static readonly RATIOS: Record<VideoFormat, number> = {
    '16:9': 16 / 9,
    '1:1': 1,
    '9:16': 9 / 16,
  };

  /**
   * Same tolerance as the image helper. The three ratios are nowhere near each
   * other, so this only decides how forgiving the edges are: a 698x710 upload
   * (0.983) reads as 1:1, which is the intent.
   */
  private static readonly RATIO_TOLERANCE = 0.02;

  /**
   * Widest a thumbnail can be and still be YouTube's "not generated" sentinel,
   * which is 120x90. Real ones start at 720 wide.
   */
  private static readonly SENTINEL_MAX_WIDTH = 120;

  /** One retry per URL, so a network blip recovers but a blocked host stops. */
  private static readonly MAX_ATTEMPTS = 2;

  // Keyed by URL rather than by video id: reading one id can take two files.
  private static readonly measured = new Map<string, Measurement>();
  private static readonly inFlight = new Set<string>();
  private static readonly failures = new Map<string, number>();

  /**
   * What format the video is, or undefined while it is still being measured —
   * the first call for an id always misses.
   */
  static readingFor(videoId: string): VideoFormatReading | undefined {
    if (!videoId) {
      return undefined;
    }

    const original = this.measure(this.originalRatioUrl(videoId));
    if (!original) {
      return undefined;
    }

    if (!this.isSentinel(original)) {
      return {
        format: this.classify(original),
        width: original.width,
        height: original.height,
        missing: false,
      };
    }

    // No original-ratio frame. Either the source was 16:9 and YouTube saw no
    // reason to render one, or the id resolves to nothing — the wide frame is
    // what tells those apart.
    const wide = this.measure(this.wideUrl(videoId));
    if (!wide) {
      return undefined;
    }

    return this.isSentinel(wide)
      ? { format: null, width: 0, height: 0, missing: true }
      : { format: '16:9', width: 0, height: 0, missing: false };
  }

  /** True while the id is still being measured. */
  static isResolving(videoId: string): boolean {
    if (!videoId) {
      return false;
    }
    return (
      this.inFlight.has(this.originalRatioUrl(videoId)) ||
      this.inFlight.has(this.wideUrl(videoId))
    );
  }

  /**
   * Best thumbnail for the video, decided by the same measurement: the
   * original-ratio frame when there is one, otherwise the clean 16:9 file.
   * Empty while measuring and when the link resolves to nothing, so a caller
   * never paints the grey sentinel.
   *
   * `hqdefault` is deliberately not a fallback — it is 480x360 with black bars
   * baked in, which show up once it is cropped into a preview.
   */
  static thumbnailFor(videoId: string): string {
    const reading = this.readingFor(videoId);
    if (!reading || reading.missing) {
      return '';
    }
    return reading.format === '16:9' ? this.wideUrl(videoId) : this.originalRatioUrl(videoId);
  }

  /**
   * Thumbnail of whichever link is in this format, or '' when none is. This is
   * how a placement finds its own creative whatever order the links were
   * pasted in.
   *
   * Every link is read, not just the ones up to the first match: reading is
   * what starts the measurement, and short-circuiting would leave
   * {@link isResolvingAny} blind to links it never asked about.
   */
  static thumbnailForFormat(videoIds: string[], format: VideoFormat): string {
    const formats = videoIds.map((videoId) => this.readingFor(videoId)?.format);
    const index = formats.indexOf(format);
    return index === -1 ? '' : this.thumbnailFor(videoIds[index]!);
  }

  /** True while any of the links is still being measured. */
  static isResolvingAny(videoIds: string[]): boolean {
    return videoIds.some((videoId) => this.isResolving(videoId));
  }

  private static classify(measurement: Measurement): VideoFormat | null {
    if (measurement.height === 0) {
      return null;
    }
    const ratio = measurement.width / measurement.height;
    const match = (Object.keys(this.RATIOS) as VideoFormat[]).find(
      (format) => Math.abs(ratio - this.RATIOS[format]) <= this.RATIO_TOLERANCE,
    );
    return match ?? null;
  }

  private static isSentinel(measurement: Measurement): boolean {
    return measurement.width <= this.SENTINEL_MAX_WIDTH;
  }

  private static originalRatioUrl(videoId: string): string {
    return `https://i.ytimg.com/vi/${videoId}/oardefault.jpg`;
  }

  private static wideUrl(videoId: string): string {
    return `https://i.ytimg.com/vi/${videoId}/hq720.jpg`;
  }

  private static measure(url: string): Measurement | undefined {
    const cached = this.measured.get(url);
    if (cached || this.inFlight.has(url)) {
      return cached;
    }
    if ((this.failures.get(url) ?? 0) >= this.MAX_ATTEMPTS) {
      return undefined;
    }

    this.inFlight.add(url);
    const image = new Image();
    image.onload = () => {
      // The sentinel lands here, not in onerror, so it is stored like any other
      // measurement and read back as "no frame of this kind".
      this.measured.set(url, { width: image.naturalWidth, height: image.naturalHeight });
      this.inFlight.delete(url);
    };
    image.onerror = () => {
      // A genuine transport failure — offline, or i.ytimg.com blocked by an
      // extension. Counted rather than poisoned outright so a blip recovers
      // without a page reload, but bounded so a blocked host is not retried on
      // every change-detection pass.
      this.inFlight.delete(url);
      this.failures.set(url, (this.failures.get(url) ?? 0) + 1);
    };
    image.src = url;
    return undefined;
  }
}
